import React, { useState } from 'react';
import { ShoppingBag, X, ChevronDown, Check, Ruler, ArrowRight, Trash2, ShieldCheck } from 'lucide-react';
import Info from './Info';

const Storefront = ({ products }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [selectedSize, setSelectedSize] = useState('M');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cart, setCart] = useState([]);

  // Logic: Add to Local Cart
  const addToCart = (product, size) => {
    const cartItem = { ...product, size, cartId: Date.now() };
    setCart([...cart, cartItem]);
    setSelectedProduct(null);
    setIsCartOpen(true);
  };

  const removeFromCart = (cartId) => {
    setCart(cart.filter(item => item.cartId !== cartId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price, 0);

  // Sorting Logic
  const sortedProducts = [...products].sort((a, b) => {
    if (sortBy === 'price-low') return a.price - b.price;
    if (sortBy === 'price-high') return b.price - a.price;
    return b.id - a.id;
  });

  return (
    <div className="bg-white min-h-screen">
      {/* NAV BAR */}
      <nav className="fixed top-0 w-full z-40 bg-white/90 backdrop-blur-md border-b border-slate-100 px-6 py-4 flex justify-between items-center">
        <div className="text-xl font-black italic uppercase tracking-tighter text-mighty-dark">
          The Mighty Name<span className="text-mighty">.</span>
        </div>
        <button 
          onClick={() => setIsCartOpen(true)}
          className="relative p-2 hover:text-mighty transition-colors"
        >
          <ShoppingBag size={20} />
          {cart.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-mighty text-white text-[8px] font-black w-4 h-4 rounded-full flex items-center justify-center">
              {cart.length}
            </span>
          )}
        </button>
      </nav>

      {/* JUMBOTRON HERO */}
      <section className="pt-32 pb-24 px-6 bg-mighty-dark text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none italic font-black text-9xl uppercase select-none flex items-center justify-center">
          Mighty // Mighty // Mighty
        </div>
        <div className="relative z-10">
          <h1 className="text-[10px] font-black tracking-[0.8em] uppercase text-mighty-light mb-6 italic">
            A Name Above Every Name
          </h1>
          <p className="text-6xl md:text-8xl font-black italic uppercase tracking-tighter leading-none mb-6">
            The Mighty Name
          </p>
          <div className="h-1.5 w-24 bg-mighty mx-auto"></div>
        </div>
      </section>

      {/* TOOLBAR */}
      <div className="max-w-7xl mx-auto px-6 mt-12 flex justify-end">
        <div className="flex items-center gap-4 border-b border-slate-100 pb-2">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Filter By:</span>
          <select 
            className="text-[10px] font-black uppercase tracking-widest outline-none bg-transparent cursor-pointer hover:text-mighty transition-colors"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="newest">Newest Arrivals</option>
            <option value="price-high">Price: High to Low</option>
            <option value="price-low">Price: Low to High</option>
          </select>
          <ChevronDown size={12} className="text-slate-400" />
        </div>
      </div>

      {/* PRODUCT GRID */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-24">
          {sortedProducts.map((product) => (
            <div key={product.id} className="group cursor-pointer" onClick={() => setSelectedProduct(product)}>
              <div className="aspect-[3/4] bg-slate-50 mb-6 overflow-hidden rounded-sm relative shadow-sm">
                <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-[1.5s] group-hover:scale-110" 
                    onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1556821840-3a63f95609a7?q=80&w=400&h=500&auto=format&fit=crop"; }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-500" />
              </div>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-black italic uppercase text-mighty-dark leading-tight group-hover:text-mighty transition-colors italic">
                    {product.name}
                  </h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Limited Edition Drop</p>
                </div>
                <div className="text-xl font-black italic text-mighty-dark">${product.price}</div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-300">
                View Details <ArrowRight size={10} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SIDE CART OVERLAY */}
      {isCartOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-mighty-dark/60 backdrop-blur-sm" onClick={() => setIsCartOpen(false)} />
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-500">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center">
              <h2 className="text-2xl font-black italic uppercase tracking-tighter">Mighty Bag</h2>
              <button onClick={() => setIsCartOpen(false)} className="hover:rotate-90 transition-transform"><X size={24}/></button>
            </div>
            
            <div className="flex-grow overflow-y-auto p-8 space-y-8">
              {cart.length === 0 ? (
                <div className="text-center py-20 flex flex-col items-center gap-4">
                  <ShoppingBag size={40} className="text-slate-100" />
                  <p className="text-slate-400 font-bold italic uppercase text-xs tracking-widest">Your bag is empty.</p>
                </div>
              ) : (
                cart.map((item) => (
                  <div key={item.cartId} className="flex gap-6 border-b border-slate-50 pb-8">
                    <img src={item.image} className="w-24 h-32 object-cover rounded-sm shadow-sm" />
                    <div className="flex-grow">
                      <h4 className="font-black italic uppercase text-sm leading-tight text-mighty-dark">{item.name}</h4>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Size: {item.size}</p>
                      <p className="font-black text-mighty mt-3 text-lg">${item.price}</p>
                    </div>
                    <button onClick={() => removeFromCart(item.cartId)} className="text-slate-200 hover:text-red-500 transition-colors self-start"><Trash2 size={18}/></button>
                  </div>
                ))
              )}
            </div>

            <div className="p-8 bg-slate-50 space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between font-black uppercase text-[10px] tracking-[0.2em] text-slate-400">
                    <span>Subtotal</span>
                    <span>${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-black uppercase text-[10px] tracking-[0.2em] text-mighty italic">
                    <span>Mission Pledge (10%)</span>
                    <span>${(cartTotal * 0.1).toFixed(2)}</span>
                </div>
              </div>
              <button className="w-full bg-mighty-dark text-white py-6 font-black uppercase text-[12px] tracking-[0.4em] hover:bg-black transition-all shadow-xl active:scale-95">
                Secure Checkout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RESTORED PRODUCT DETAIL MODAL */}
      {selectedProduct && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-12 animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-mighty-dark/90 backdrop-blur-md" onClick={() => setSelectedProduct(null)} />
          
          <div className="bg-white w-full max-w-5xl h-fit max-h-[95vh] overflow-y-auto relative z-10 grid md:grid-cols-2 shadow-2xl rounded-sm">
            <button 
              onClick={() => setSelectedProduct(null)}
              className="absolute top-6 right-6 z-20 hover:rotate-90 transition-transform p-2 bg-white/80 rounded-full shadow-lg"
            >
              <X size={20} />
            </button>

            {/* Left: Product Image */}
            <div className="bg-slate-50 sticky top-0">
              <img 
                src={selectedProduct.image} 
                className="w-full h-full object-cover"
                alt={selectedProduct.name}
              />
            </div>

            {/* Right: Info & Size Selection */}
            <div className="p-8 md:p-16 flex flex-col justify-center">
              <h2 className="text-4xl md:text-5xl font-black italic uppercase text-mighty-dark mb-2 tracking-tighter leading-none">
                {selectedProduct.name}
              </h2>
              <p className="text-2xl font-black italic text-mighty mb-10">${selectedProduct.price}</p>
              
              {/* SIZE PICKER */}
              <div className="mb-10">
                <div className="flex justify-between items-center mb-4">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Select Size</label>
                  <button className="text-[9px] font-black uppercase tracking-widest text-mighty flex items-center gap-1 hover:underline underline-offset-4">
                    <Ruler size={10} /> Size Guide
                  </button>
                </div>
                <div className="flex gap-3">
                  {['S', 'M', 'L', 'XL', 'XXL'].map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`w-12 h-12 flex items-center justify-center font-black transition-all border-2 text-xs
                        ${selectedSize === size 
                          ? 'border-mighty-dark bg-mighty-dark text-white' 
                          : 'border-slate-100 text-slate-300 hover:border-mighty hover:text-mighty'}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* RESTORED PRODUCT HIGHLIGHTS */}
              <div className="space-y-4 mb-10 pb-10 border-b border-slate-100">
                <p className="text-slate-500 text-sm italic font-medium leading-relaxed mb-6">
                  Engineered for presence. Featuring our signature <span className="text-mighty-dark font-black tracking-tight underline decoration-mighty decoration-2 underline-offset-4">MIGHTY-WEIGHT</span> cotton blend for a structure that lasts.
                </p>
                <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                    <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-700">
                        <Check size={14} className="text-mighty" strokeWidth={3} /> Dropped Shoulder
                    </div>
                    <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-700">
                        <Check size={14} className="text-mighty" strokeWidth={3} /> 450GSM Fleece
                    </div>
                    <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-700">
                        <Check size={14} className="text-mighty" strokeWidth={3} /> Oversized Fit
                    </div>
                    <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-700">
                        <ShieldCheck size={14} className="text-mighty" strokeWidth={3} /> 10% Impact Fund
                    </div>
                </div>
              </div>

              {/* ACTION BUTTON */}
              <button 
                onClick={() => addToCart(selectedProduct, selectedSize)}
                className="w-full bg-mighty-dark text-white py-6 font-black uppercase text-[12px] tracking-[0.4em] hover:bg-mighty transition-all shadow-xl flex items-center justify-center gap-4 group active:scale-95"
              >
                <ShoppingBag size={18} className="group-hover:animate-bounce" /> Add to Bag (Size {selectedSize})
              </button>
              
              <p className="mt-8 text-center text-[9px] font-bold text-slate-300 uppercase tracking-[0.3em]">
                Secure Checkout // Worldwide Priority
              </p>
            </div>
          </div>
        </div>
      )}

      <Info />
    </div>
  );
};

export default Storefront;